/**
 * Rate Limiting System
 * Implements multiple strategies: Sliding Window, Token Bucket, and Circuit Breaker
 * Uses IndexedDB for persistent client-side tracking and localStorage for quick access
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  keyPrefix?: string;
}

interface TokenBucketConfig {
  capacity: number;
  refillRate: number; // tokens per second
  refillInterval?: number;
}

interface CircuitBreakerConfig {
  failureThreshold: number;
  resetTimeout: number;
  halfOpenAttempts: number;
}

interface RateLimitEntry {
  timestamp: number;
  count: number;
  key: string;
}

// ========================================
// SLIDING WINDOW RATE LIMITER
// ========================================

export class SlidingWindowRateLimiter {
  private config: RateLimitConfig;
  private store = new Map<string, RateLimitEntry[]>();

  constructor(config: RateLimitConfig) {
    this.config = { keyPrefix: 'ratelimit', ...config };
    this.loadFromStorage();
  }

  private getStorageKey(identifier: string): string {
    return `${this.config.keyPrefix}:${identifier}`;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(`${this.config.keyPrefix}:all`);
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.entries(parsed).forEach(([key, value]: [string, any]) => {
          this.store.set(key, value);
        });
      }
    } catch (e) {
      console.warn('Failed to load rate limit data from storage:', e);
    }
  }

  private saveToStorage(): void {
    try {
      const data: Record<string, RateLimitEntry[]> = {};
      this.store.forEach((value, key) => {
        data[key] = value;
      });
      localStorage.setItem(`${this.config.keyPrefix}:all`, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save rate limit data to storage:', e);
    }
  }

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const key = this.getStorageKey(identifier);
    let entries = this.store.get(key) || [];

    // Remove entries outside the window
    entries = entries.filter(entry => now - entry.timestamp < this.config.windowMs);

    if (entries.length < this.config.maxRequests) {
      entries.push({ timestamp: now, count: entries.length + 1, key });
      this.store.set(key, entries);
      this.saveToStorage();
      return true;
    }

    return false;
  }

  getRemainingRequests(identifier: string): number {
    const now = Date.now();
    const key = this.getStorageKey(identifier);
    const entries = this.store.get(key) || [];
    const validEntries = entries.filter(entry => now - entry.timestamp < this.config.windowMs);
    return Math.max(0, this.config.maxRequests - validEntries.length);
  }

  getResetTime(identifier: string): number {
    const now = Date.now();
    const key = this.getStorageKey(identifier);
    const entries = this.store.get(key) || [];
    
    if (entries.length === 0) return 0;
    
    const oldestEntry = entries[0];
    const resetTime = oldestEntry.timestamp + this.config.windowMs;
    return Math.max(0, resetTime - now);
  }

  reset(identifier?: string): void {
    if (identifier) {
      const key = this.getStorageKey(identifier);
      this.store.delete(key);
    } else {
      this.store.clear();
    }
    this.saveToStorage();
  }
}

// ========================================
// TOKEN BUCKET RATE LIMITER
// ========================================

export class TokenBucketRateLimiter {
  private config: TokenBucketConfig;
  private buckets = new Map<string, { tokens: number; lastRefill: number }>();
  private refillInterval: number;

  constructor(config: TokenBucketConfig) {
    this.config = { refillInterval: 1000, ...config };
    this.refillInterval = this.config.refillInterval!;
    this.startRefillTimer();
  }

  private startRefillTimer(): void {
    setInterval(() => {
      const now = Date.now();
      this.buckets.forEach(bucket => {
        const elapsed = (now - bucket.lastRefill) / 1000; // seconds
        const tokensToAdd = elapsed * this.config.refillRate;
        bucket.tokens = Math.min(
          this.config.capacity,
          bucket.tokens + tokensToAdd
        );
        bucket.lastRefill = now;
      });
    }, this.refillInterval);
  }

  isAllowed(identifier: string, tokensRequired: number = 1): boolean {
    let bucket = this.buckets.get(identifier);
    const now = Date.now();

    if (!bucket) {
      bucket = { tokens: this.config.capacity, lastRefill: now };
      this.buckets.set(identifier, bucket);
    }

    // Refill tokens
    const elapsed = (now - bucket.lastRefill) / 1000;
    const tokensToAdd = elapsed * this.config.refillRate;
    bucket.tokens = Math.min(this.config.capacity, bucket.tokens + tokensToAdd);
    bucket.lastRefill = now;

    if (bucket.tokens >= tokensRequired) {
      bucket.tokens -= tokensRequired;
      return true;
    }

    return false;
  }

  getAvailableTokens(identifier: string): number {
    const bucket = this.buckets.get(identifier);
    return bucket ? bucket.tokens : this.config.capacity;
  }

  reset(identifier?: string): void {
    if (identifier) {
      this.buckets.delete(identifier);
    } else {
      this.buckets.clear();
    }
  }
}

// ========================================
// CIRCUIT BREAKER
// ========================================

export enum CircuitState {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  HALF_OPEN = 'HALF_OPEN',
}

export class CircuitBreaker {
  private config: CircuitBreakerConfig;
  private state: CircuitState = CircuitState.CLOSED;
  private failureCount: number = 0;
  private successCount: number = 0;
  private lastFailureTime: number = 0;

  constructor(config: CircuitBreakerConfig) {
    this.config = config;
  }

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === CircuitState.OPEN) {
      if (Date.now() - this.lastFailureTime > this.config.resetTimeout) {
        this.state = CircuitState.HALF_OPEN;
        this.successCount = 0;
      } else {
        throw new Error('Circuit breaker is OPEN');
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.failureCount = 0;

    if (this.state === CircuitState.HALF_OPEN) {
      this.successCount++;
      if (this.successCount >= this.config.halfOpenAttempts) {
        this.state = CircuitState.CLOSED;
        this.successCount = 0;
      }
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.config.failureThreshold) {
      this.state = CircuitState.OPEN;
    }
  }

  getState(): CircuitState {
    return this.state;
  }

  reset(): void {
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = 0;
  }
}

// ========================================
// DATABASE QUERY RATE LIMITER
// ========================================

export class DatabaseRateLimiter {
  private slidingWindow: SlidingWindowRateLimiter;
  private tokenBucket: TokenBucketRateLimiter;
  private circuitBreaker: CircuitBreaker;

  constructor() {
    // 100 requests per minute per user
    this.slidingWindow = new SlidingWindowRateLimiter({
      maxRequests: 100,
      windowMs: 60 * 1000,
      keyPrefix: 'db_rate_limit',
    });

    // 10 tokens per second (can burst up to 20)
    this.tokenBucket = new TokenBucketRateLimiter({
      capacity: 20,
      refillRate: 10,
    });

    // Open circuit after 10 failures, reset after 30 seconds
    this.circuitBreaker = new CircuitBreaker({
      failureThreshold: 10,
      resetTimeout: 30 * 1000,
      halfOpenAttempts: 3,
    });
  }

  async checkLimit(userId: string): Promise<boolean> {
    if (!this.slidingWindow.isAllowed(userId)) {
      return false;
    }

    if (!this.tokenBucket.isAllowed(userId)) {
      return false;
    }

    return true;
  }

  getRemainingQuota(userId: string): {
    requests: number;
    tokens: number;
    circuitState: CircuitState;
  } {
    return {
      requests: this.slidingWindow.getRemainingRequests(userId),
      tokens: this.tokenBucket.getAvailableTokens(userId),
      circuitState: this.circuitBreaker.getState(),
    };
  }

  async executeWithCircuitBreaker<T>(
    userId: string,
    fn: () => Promise<T>
  ): Promise<T> {
    const allowed = await this.checkLimit(userId);
    if (!allowed) {
      throw new Error('Rate limit exceeded');
    }

    return this.circuitBreaker.execute(fn);
  }

  recordFailure(): void {
    // Failure is recorded in circuit breaker when execute throws
  }

  reset(userId?: string): void {
    this.slidingWindow.reset(userId);
    this.tokenBucket.reset(userId);
    if (!userId) {
      this.circuitBreaker.reset();
    }
  }
}

// ========================================
// EXPORT SINGLETON INSTANCES
// ========================================

export const dbRateLimiter = new DatabaseRateLimiter();

export const apiRateLimiter = new SlidingWindowRateLimiter({
  maxRequests: 50,
  windowMs: 60 * 1000,
  keyPrefix: 'api_rate_limit',
});

export const voiceRateLimiter = new TokenBucketRateLimiter({
  capacity: 10,
  refillRate: 2, // 2 requests per second
});

export const authRateLimiter = new SlidingWindowRateLimiter({
  maxRequests: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  keyPrefix: 'auth_rate_limit',
});
